import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  signIn,
  completeNewPassword,
  forgotPassword,
  confirmPassword,
  signOut,
  type CognitoUser,
} from '../auth/cognito';
import { useAuth } from '../store/auth';
import { api, ApiError } from '../api/client';
import {
  newSessionId,
  getStoredSessionId,
  storeSessionId,
  clearStoredSessionId,
} from '../auth/sessionId';
import { SessionTakeoverModal } from '../components/SessionTakeoverModal';

interface PendingClaim {
  email: string;
  user: { user: import('../auth/cognito').AuthUser };
  idToken: string;
  accessToken: string;
  expiresAt: number;
  sessionId: string;
  /** Differentiate "another active session" (prompt) from "active call elsewhere" (terminal). */
  reason: 'active_session_elsewhere' | 'active_call_elsewhere';
}

type Mode = 'signIn' | 'newPassword' | 'forgotRequest' | 'forgotConfirm';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuth(s => s.setSession);
  const redirectTo = (location.state as LocationState)?.from?.pathname ?? '/';

  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [pendingUser, setPendingUser] = useState<{ user: CognitoUser; attrs: Record<string, string> } | undefined>();
  const [pendingClaim, setPendingClaim] = useState<PendingClaim | undefined>();
  const [takeoverBusy, setTakeoverBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  // FR-66: one-shot banner when we landed here after being kicked.
  const [signedOutReason] = useState<string | undefined>(() => {
    try {
      const r = window.sessionStorage.getItem('lumi:signedOutReason') ?? undefined;
      if (r) window.sessionStorage.removeItem('lumi:signedOutReason');
      return r;
    } catch {
      return undefined;
    }
  });
  const [info, setInfo] = useState<string | undefined>(() =>
    signedOutReason === 'kicked'
      ? 'You were signed out because your account signed in on another device.'
      : undefined,
  );

  function reset() {
    setError(undefined);
    setInfo(undefined);
  }

  /**
   * Sign-in finalisation. We have tokens; now claim the FR-66 session id
   * before letting the user into the app.
   *
   *   • 200 → store tokens + sessionId, navigate
   *   • 409 active_session_elsewhere → stash, show takeover modal
   *   • 409 active_call_elsewhere    → stash, show terminal modal
   *
   * setSession is deliberately deferred until after a successful claim
   * (or force-claim) so the auth store never holds tokens for an
   * unclaimed session — RouteGuard would let them into protected pages
   * even though their first API call would 401.
   */
  async function finaliseSignIn(result: {
    user: import('../auth/cognito').AuthUser;
    idToken: string;
    accessToken: string;
    expiresAt: number;
  }): Promise<void> {
    const sessionId = getStoredSessionId() ?? newSessionId();

    // Manually inject tokens into the auth store *just for the duration
    // of the claim call* so the api/client wrapper picks up
    // Authorization + X-Session-Id headers. We'll either confirm or
    // clear it based on the response.
    useAuth.setState({
      idToken: result.idToken,
      accessToken: result.accessToken,
      sessionId,
    });

    try {
      await api.claimSession(sessionId, false);
      storeSessionId(sessionId);
      setSession({
        user: result.user,
        idToken: result.idToken,
        accessToken: result.accessToken,
        expiresAt: result.expiresAt,
        sessionId,
      });
      navigate(redirectTo, { replace: true });
      return;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | undefined;
        const reason =
          body?.error === 'active_call_elsewhere'
            ? 'active_call_elsewhere'
            : 'active_session_elsewhere';
        // Wash the half-set auth state — the claim didn't succeed.
        useAuth.getState().clear();
        setPendingClaim({
          email: result.user.email,
          user: { user: result.user },
          idToken: result.idToken,
          accessToken: result.accessToken,
          expiresAt: result.expiresAt,
          sessionId,
          reason,
        });
        return;
      }
      // Unexpected — wash state and surface.
      useAuth.getState().clear();
      throw err;
    }
  }

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    reset();
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'newPasswordRequired') {
        setPendingUser({ user: result.cognitoUser, attrs: result.userAttributes });
        setMode('newPassword');
        setInfo('First-time login: please choose a permanent password.');
      } else {
        await finaliseSignIn(result);
      }
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    reset();
    if (!pendingUser) return;
    setSubmitting(true);
    try {
      const result = await completeNewPassword(pendingUser.user, newPassword, pendingUser.attrs);
      await finaliseSignIn(result);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  /** User clicked "Yes, sign in here" on the takeover modal. */
  async function handleTakeoverConfirm(): Promise<void> {
    if (!pendingClaim) return;
    setTakeoverBusy(true);
    try {
      // Force-claim writes a NEW session id (other device gets kicked
      // on its next API call). Store the new id in localStorage so
      // subsequent reloads of this browser stay on it.
      const freshSid = newSessionId();
      useAuth.setState({
        idToken: pendingClaim.idToken,
        accessToken: pendingClaim.accessToken,
        sessionId: freshSid,
      });
      await api.claimSession(freshSid, true);
      storeSessionId(freshSid);
      setSession({
        user: pendingClaim.user.user,
        idToken: pendingClaim.idToken,
        accessToken: pendingClaim.accessToken,
        expiresAt: pendingClaim.expiresAt,
        sessionId: freshSid,
      });
      setPendingClaim(undefined);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      useAuth.getState().clear();
      setPendingClaim(undefined);
      setError(toMessage(err));
    } finally {
      setTakeoverBusy(false);
    }
  }

  /** User clicked "No" / "OK" on the takeover modal — discard the sign-in. */
  function handleTakeoverCancel(): void {
    // Cognito's local SDK already stored the tokens (it does so as part
    // of authenticateUser succeeding). Sign them out so the SDK forgets
    // them and the next sign-in attempt starts fresh. Also clear our
    // localStorage session id so we don't accidentally try to reuse it.
    signOut();
    clearStoredSessionId();
    useAuth.getState().clear();
    setPendingClaim(undefined);
  }

  async function handleForgotRequest(e: FormEvent) {
    e.preventDefault();
    reset();
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setMode('forgotConfirm');
      setInfo('Check your email for the verification code.');
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotConfirm(e: FormEvent) {
    e.preventDefault();
    reset();
    setSubmitting(true);
    try {
      await confirmPassword(email, code, newPassword);
      setMode('signIn');
      setInfo('Password reset. Please sign in with your new password.');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <h1 className="text-xl font-semibold text-slate-900 text-center">Lumi Agent Console</h1>
        <p className="text-sm text-slate-500 text-center mt-1">{titleFor(mode)}</p>

        {error && <Banner kind="error">{error}</Banner>}
        {info && <Banner kind="info">{info}</Banner>}

        {mode === 'signIn' && (
          <form onSubmit={handleSignIn} className="mt-6 space-y-4">
            <Field label="Email" type="email" value={email} onChange={setEmail} autoFocus required />
            <Field label="Password" type="password" value={password} onChange={setPassword} required />
            <SubmitButton submitting={submitting}>Sign in</SubmitButton>
            <button
              type="button"
              onClick={() => { reset(); setMode('forgotRequest'); }}
              className="text-xs text-slate-500 hover:text-slate-700 w-full text-center"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === 'newPassword' && (
          <form onSubmit={handleNewPassword} className="mt-6 space-y-4">
            <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} autoFocus required minLength={12} />
            <p className="text-xs text-slate-500">At least 12 characters, mixed case, with a number.</p>
            <SubmitButton submitting={submitting}>Set password & sign in</SubmitButton>
          </form>
        )}

        {mode === 'forgotRequest' && (
          <form onSubmit={handleForgotRequest} className="mt-6 space-y-4">
            <Field label="Email" type="email" value={email} onChange={setEmail} autoFocus required />
            <SubmitButton submitting={submitting}>Send reset code</SubmitButton>
            <button type="button" onClick={() => { reset(); setMode('signIn'); }} className="text-xs text-slate-500 hover:text-slate-700 w-full text-center">
              Back to sign in
            </button>
          </form>
        )}

        {mode === 'forgotConfirm' && (
          <form onSubmit={handleForgotConfirm} className="mt-6 space-y-4">
            <Field label="Verification code" value={code} onChange={setCode} autoFocus required />
            <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} required minLength={12} />
            <SubmitButton submitting={submitting}>Reset password</SubmitButton>
            <button type="button" onClick={() => { reset(); setMode('signIn'); }} className="text-xs text-slate-500 hover:text-slate-700 w-full text-center">
              Cancel
            </button>
          </form>
        )}
      </div>
      {pendingClaim && (
        pendingClaim.reason === 'active_session_elsewhere' ? (
          <SessionTakeoverModal
            kind="prompt"
            email={pendingClaim.email}
            onConfirm={handleTakeoverConfirm}
            onClose={handleTakeoverCancel}
            busy={takeoverBusy}
          />
        ) : (
          <SessionTakeoverModal
            kind="blocked"
            email={pendingClaim.email}
            onClose={handleTakeoverCancel}
          />
        )
      )}
    </main>
  );
}

function titleFor(mode: Mode): string {
  switch (mode) {
    case 'signIn': return 'Sign in to continue';
    case 'newPassword': return 'Set a permanent password';
    case 'forgotRequest': return 'Reset your password';
    case 'forgotConfirm': return 'Enter the code from your email';
  }
}

function toMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function Field(props: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoFocus?: boolean;
  minLength?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        required={props.required}
        autoFocus={props.autoFocus}
        minLength={props.minLength}
        className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
      />
    </label>
  );
}

function SubmitButton({ submitting, children }: { submitting: boolean; children: React.ReactNode }) {
  return (
    <button
      type="submit"
      disabled={submitting}
      className="w-full rounded-lg bg-brand text-white py-2 text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
    >
      {submitting ? '…' : children}
    </button>
  );
}

function Banner({ kind, children }: { kind: 'error' | 'info'; children: React.ReactNode }) {
  const cls = kind === 'error'
    ? 'bg-red-50 text-red-800 border-red-200'
    : 'bg-blue-50 text-blue-800 border-blue-200';
  return <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${cls}`}>{children}</div>;
}
